import React, { FC, Suspense, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePayoutEligibility } from '@hub/api/queries';
import { usePreloadTranslations } from '@hub/features/account-credentials-button';
import { useWidgetManager } from '@hub/features/account-dashboard/hooks';
import { EnvService } from '@hub/services/core/env-service';
import { Box, useTheme } from '@mui/material';
import { FORBIDDEN_PRACTICES_LINKS } from '@monorepo/client-types';
import { DashboardCard, useBreakpoints } from '@monorepo/nova-ui';
import { PayoutSource } from '@monorepo/types';
import { useDashboard } from '../../../../../contexts';
import { StyledAccountCredentialsButton } from '..';
import { mapWidgetToComponent } from '../objective-list/utils/widget-mapper';
import { WidgetItem } from '../objective-list/widget-item';
import { StyledForbiddenPracticesLink } from './styled-forbidden-practices-link';

export const WidgetList: FC = () => {
  const { account, accountBalance, program, profitableDays, assetType } =
    useDashboard();
  const { t } = useTranslation(['dashboardTemplates', 'dashboardHeader']);
  const application = EnvService.application;
  const theme = useTheme();
  const { isTabletOrMobile } = useBreakpoints();

  // Preload translation namespaces to prevent UI refresh when modal opens
  usePreloadTranslations(['login', 'dashboardTemplates']);

  // Fetch payout eligibility data for payout widget
  const { data: eligibilityData } = usePayoutEligibility(
    PayoutSource.Account,
    account?._id || '',
    account || undefined, // Pass account for polling control
  );

  // Memoize widget data to prevent unnecessary recalculations
  const widgetData = useMemo(
    () => ({
      account: account || undefined,
      accountBalance: accountBalance || undefined,
      program: program || null,
      eligibilityData,
      profitableDays: profitableDays || null,
    }),
    [account, accountBalance, program, eligibilityData, profitableDays],
  );

  // Get widget configurations from widget manager
  const { widgets, isEmpty, error } = useWidgetManager({
    assetType: assetType ?? null,
    widgetData,
  });

  // Handle empty state or missing asset type
  if (isEmpty || !assetType) {
    return null;
  }

  // Handle error state
  if (error) {
    return null;
  }

  return (
    <DashboardCard
      minWidth={{
        xs: 100,
        sm: 150,
        md: 200,
      }}
      position="relative"
      alignItems="stretch"
      sx={(theme) => ({
        paddingLeft: {
          xs: theme.spacing(2.5),
          sm: theme.spacing(3),
        },
        paddingRight: {
          xs: theme.spacing(2.5),
          sm: theme.spacing(5),
        },
        paddingTop: {
          xs: theme.spacing(2.5),
          sm: theme.spacing(3),
        },
        paddingBottom: {
          xs: theme.spacing(2.5),
          sm: theme.spacing(3),
        },
        borderRadius: theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        height: {
          xs: 'auto',
          sm: '100%',
        },
      })}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: {
            xs: theme.spacing(2),
            sm: theme.spacing(3),
          },
          width: '100%',
          flex: {
            xs: 'none',
            sm: 1,
          },
          alignItems: {
            xs: 'flex-start',
            sm: 'stretch',
          },
          justifyContent: {
            xs: 'flex-start',
            sm: 'space-between',
          },
        }}
      >
        {widgets.map((widgetConfig) => {
          const widgetData = mapWidgetToComponent(
            widgetConfig.type,
            widgetConfig.props,
          );
          return widgetData ? (
            <WidgetItem key={widgetConfig.type} {...widgetData} />
          ) : null;
        })}
      </Box>

      <StyledForbiddenPracticesLink
        href={FORBIDDEN_PRACTICES_LINKS[application]}
        target="_blank"
        rel="noopener noreferrer"
      >
        {t('dashboardTemplates:forbidden_practices')}
      </StyledForbiddenPracticesLink>

      {account && !isTabletOrMobile && (
        <Suspense fallback={null}>
          <StyledAccountCredentialsButton
            account={{
              tsAccount: account?.tsAccount || '',
              tradingSystem: account?.tradingSystem || '',
              tsUserId: account?.tsUserId || '',
              tsAccountId: account?.tsAccountId || '',
            }}
            useNovaModal={true}
          />
        </Suspense>
      )}
    </DashboardCard>
  );
};
